/* eslint-disable no-param-reassign */
import React, {
  ChangeEvent,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  BiCopyAlt as DraggingIcon,
  BiXCircle as ErrorIcon,
  BiTrash as TrashIcon,
  BiPencil as EditIcon,
} from 'react-icons/bi';
import Compressor from 'compressorjs';
// eslint-disable-next-line import/no-extraneous-dependencies
import filesize from 'filesize';

import { Wrapper, Container, Content, ManagePreviewImage } from './styles';
import { useToast } from '../../hooks/toast';

import DefaultImg from '../../assets/no_image.png';

type FileInputProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  icon: React.FunctionComponent;
  iconSize?: number;
  title?: string;
  label?: string;
  subtitle?: string;
  selectedImage?: File | string;
  acceptableTypes?: string;
  containerStyle?: React.CSSProperties;
  onSelectFile(file: File): void;
  onRemove(): void;
};

const FileInput: React.FC<FileInputProps> = ({
  id,
  label,
  subtitle,
  iconSize = 60,
  selectedImage,
  acceptableTypes,
  containerStyle,
  onSelectFile,
  onRemove,
  icon: DefaultIcon,
  title: defaultTitle,
  ...rest
}) => {
  const fileInputRef = useRef<HTMLLabelElement>(null);

  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const { addToast } = useToast();

  function handleSelectedImage(): string | undefined {
    if (selectedImage instanceof File) {
      return URL.createObjectURL(selectedImage);
    }

    return `${process.env.REACT_APP_SERVER_URL}/files/${selectedImage}`;
  }

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (
        event.dataTransfer &&
        event.dataTransfer.items &&
        event.dataTransfer.items.length > 0
      ) {
        setIsDragging(true);

        if (!acceptableTypes) {
          return;
        }

        const fileToAttach = event.dataTransfer.items[0];

        const fileTypeToAttach = String('.').concat(
          fileToAttach.type.split('/')[1],
        );

        const hasAnAcceptableType = acceptableTypes
          .split(',')
          .includes(fileTypeToAttach);

        if (!hasAnAcceptableType) {
          setError('Formato não suportado');
        }
      }
    },
    [acceptableTypes],
  );

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);
    setError('');
  }, []);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleCompressedUpload = useCallback(
    (_file: File): void => {
      // eslint-disable-next-line no-new
      new Compressor(_file, {
        quality: 0.6,
        success(result) {
          const newFile = new File([result], _file.name);
          onSelectFile(newFile);
        },
        error(err) {
          onSelectFile(_file);
          console.log(err.message);
        },
      });
    },
    [onSelectFile],
  );

  const handleDrop = useCallback(
    async (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setIsDragging(false);
      setError('');

      if (
        event.dataTransfer &&
        event.dataTransfer.files &&
        event.dataTransfer.files.length > 0
      ) {
        const file = event.dataTransfer.files[0];

        let fileSize = Number(filesize(file.size).split(' ')[0]);
        const fileType = filesize(file.size).split(' ')[1];

        if (fileType === 'MB') {
          fileSize *= 1000;
        }

        if (fileSize > 10000) {
          addToast({
            type: 'error',
            title: `Imagem ${file.name} não foi adicionada. Ultrapassou o tamanho limite de 10 MB.`,
          });
          return;
        }

        handleCompressedUpload(file);
        event.dataTransfer.clearData();
      }
    },
    [addToast, handleCompressedUpload],
  );

  const handleSelectFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || !event.target.files[0]) {
        return;
      }

      const file = event.target.files[0];
      event.target.value = '';

      let fileSize = Number(filesize(file.size).split(' ')[0]);
      const fileType = filesize(file.size).split(' ')[1];

      if (fileType === 'MB') {
        fileSize *= 1000;
      }

      if (fileSize > 10000) {
        addToast({
          type: 'error',
          title: `Imagem ${file.name} não foi adicionada. Ultrapassou o tamanho limite de 10 MB.`,
        });
        return;
      }

      handleCompressedUpload(file);
    },
    [addToast, handleCompressedUpload],
  );

  const handleChangeFile = useCallback(() => {
    const fileInputElement = fileInputRef.current;

    if (fileInputElement) {
      fileInputElement.click();
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    onRemove();
    (document.getElementById(id) as HTMLInputElement).value = '';
  }, [id, onRemove]);

  useEffect(() => {
    const fileInputElement = fileInputRef.current;

    if (fileInputElement) {
      fileInputElement.addEventListener('dragenter', handleDragEnter);
      fileInputElement.addEventListener('dragleave', handleDragLeave);
      fileInputElement.addEventListener('dragover', handleDragOver);
      fileInputElement.addEventListener('drop', handleDrop);
    }

    return () => {
      if (fileInputElement) {
        fileInputElement.removeEventListener('dragenter', handleDragEnter);
        fileInputElement.removeEventListener('dragleave', handleDragLeave);
        fileInputElement.removeEventListener('dragover', handleDragOver);
        fileInputElement.removeEventListener('drop', handleDrop);
      }
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  const Icon = isDragging ? DraggingIcon : DefaultIcon;
  const title = isDragging ? 'Solte para anexar' : defaultTitle;

  return (
    <Wrapper style={containerStyle}>
      {label && <label htmlFor={id}>{label}</label>}
      <Container isDragging={isDragging} hasError={!!error}>
        {selectedImage && (
          <>
            <ManagePreviewImage>
              <button type="button" onClick={handleChangeFile}>
                <EditIcon />
              </button>
              <button type="button" onClick={handleRemoveFile}>
                <TrashIcon />
              </button>
            </ManagePreviewImage>
            <img
              src={handleSelectedImage()}
              alt=""
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src = DefaultImg;
              }}
            />
          </>
        )}
        <Content>
          {error ? <ErrorIcon size={iconSize} /> : <Icon size={iconSize} />}
          {title && <p>{error || title}</p>}
          {subtitle && <span>{subtitle}</span>}
        </Content>
        <label ref={fileInputRef} htmlFor={id}>
          <input
            {...rest}
            type="file"
            id={id}
            accept={acceptableTypes}
            onChange={handleSelectFile}
            multiple={false}
          />
        </label>
      </Container>
    </Wrapper>
  );
};

export default FileInput;
